<template>
  <div>
    <page-header :actions="headerActions" :page="page" />
    <board class="mb-4" :details="board" />
    <validation-observer ref="dispensationRequestRef" novalidate tag="form">
      <div>
        <section-title :has-line="true" title="Información de la Dispensa" />
        <div class="row mb-3">
          <div class="col-3">
            <validation-provider
              v-slot="{ errors }"
              :rules="!readOnly ? 'required' : ''"
            >
              <custom-date-picker
                v-model="dispensationInfo.selectedMinDate"
                custom-error-msg="Favor de seleccionar la fecha desde."
                :disabled="readOnly"
                :error="errors[0]"
                label="Fecha Desde:"
                :max-date="new Date(maxDateToValidate)"
                :min-date="exemptionMinDate"
                placeholder="DD/MM/YYYY"
              />
              <error
                v-if="requiredValidRangeDate && errors[0] == null"
                :message="requiredValidRangeDate"
                variant="no-border mt-2 ml-n1"
              />
            </validation-provider>
          </div>
          <div class="col-3">
            <validation-provider
              v-slot="{ errors }"
              :rules="!readOnly ? 'required' : ''"
            >
              <custom-date-picker
                v-model="dispensationInfo.selectedMaxDate"
                custom-error-msg="Favor de seleccionar la fecha hasta."
                :disabled="readOnly"
                :error="errors[0]"
                label="Fecha Hasta:"
                :max-date="new Date(maxDateToValidate)"
                :min-date="exemptionMinDate"
                placeholder="DD/MM/YYYY"
              />
            </validation-provider>
          </div>
        </div>
        <div class="row mb-4">
          <div class="col">
            <validation-provider
              v-slot="{ errors }"
              :rules="!readOnly ? 'required' : ''"
            >
              <custom-text-area
                v-model="dispensationInfo.exemptionComment"
                custom-error-msg="Favor de introducir un comentario."
                :disabled="readOnly"
                :error="errors[0]"
                label="Comentario:"
                :maxlength="500"
              />
            </validation-provider>
          </div>
        </div>
      </div>
      <dispense-documents
        v-if="isDEPRRequest"
        ref="dispenseDocumentsRef"
        :inner-exemption-files="dispensationInfo.exemptionFiles"
      />
      <template v-else>
        <div>
          <section-title
            :has-line="true"
            :title="
              `Información del Especialista${
                isDeprExemption ? '' : ' Supervisor'
              }`
            "
          />
          <callout
            border-color="primary-2"
            class-main="bg-white border-bottom mb-4"
            :has-toggle="false"
          >
            <div class="row">
              <div class="col-md-5">
                <p>
                  Describa si bajo su corporación realiza las siguientes
                  labores:
                </p>
                <error
                  v-if="hasSameCorpRoles && requiredSameCorpRolMessage"
                  :message="requiredSameCorpRolMessage"
                  variant="no-border"
                />
              </div>
              <div class="col-md-7 d-flex">
                <div
                  v-for="(item, index) in specialistSupervisorTaskOne"
                  :key="`task_one_${index}`"
                  class="col-sm"
                >
                  <check-box
                    v-model="item.checked"
                    :disabled="readOnly"
                    :text="item.name"
                  />
                </div>
              </div>
            </div>
          </callout>
          <callout
            border-color="primary-2"
            class-main="bg-white border-bottom mb-4"
            :has-toggle="false"
          >
            <div class="row">
              <div class="col-md-5">
                <p>
                  Describa cual de las siguientes labores realiza bajo otra
                  corporación:
                </p>
                <error
                  v-if="hasOtherCorpRoles && requiredOtherCorpRolMessage"
                  :message="requiredOtherCorpRolMessage"
                  variant="no-border"
                />
              </div>
              <div class="col-md-7 d-flex">
                <div
                  v-for="(item, index) in specialistSupervisorTaskTwo"
                  :key="`task_two_${index}`"
                  class="col-sm"
                >
                  <check-box
                    v-model="item.checked"
                    :disabled="readOnly"
                    :text="item.name"
                  />
                </div>
              </div>
            </div>
          </callout>
          <div class="container-fluid mb-4 bg-secondary-13 py-2 rounded">
            <div class="row border-bottom mt-3">
              <div class="col-sm">
                <p class="data-name">
                  {{
                    `Total de matrícula${
                      isDeprExemption ? '' : ' del supervisor'
                    } en su corporación:`
                  }}
                  <span class="data">
                    {{
                      dispensationInfo.supervisorInfo != undefined
                        ? dispensationInfo.supervisorInfo.serviceTotal
                        : '-'
                    }}
                  </span>
                </p>
              </div>
              <div class="col-sm">
                <p class="data-name">
                  {{
                    `Total de matrícula ${
                      isDeprExemption ? '' : ' del supervisor'
                    } incluyendo otras
                  corporaciones:`
                  }}
                  <span class="data">
                    {{
                      dispensationInfo.supervisorInfo != undefined
                        ? dispensationInfo.supervisorInfo.corpServiceTotal
                        : '-'
                    }}
                  </span>
                </p>
              </div>
            </div>
            <div class="row mt-3">
              <div class="col">
                <p class="data-name">
                  Especialidades:
                  <span class="data">
                    {{
                      dispensationInfo.supervisorInfo != undefined
                        ? dispensationInfo.specialistSpecialtyTypeNames
                        : '-'
                    }}
                  </span>
                </p>
              </div>
            </div>
          </div>
        </div>
        <section-title
          :has-line="true"
          :title="
            `Información de Especialistas${
              isDeprExemption ? '' : ' Supervisados'
            }`
          "
        />
        <supervised
          :dispense-request-list="
            dispensationInfo.exemptionRequestSpecialistDetail
          "
          :supervised-other-corporation-list="
            dispensationInfo.exemptionRequestSupervisorOtherCompanyDetail
          "
          :supervisor-info="dispensationInfo.supervisorInfo"
        />
        <div v-if="dispensationInfo?.exemptionFiles?.length">
          <section-title
            class="mt-3"
            :has-line="true"
            title="Documentos de Respaldo"
          />
          <div
            v-for="exemptionFile in dispensationInfo.exemptionFiles"
            :key="exemptionFile.fileId"
          >
            <callout class-main="mb-2 py-3" :has-toggle="false">
              <div class="col-12 d-flex justify-content-between">
                <div>
                  <span class="exemption-file__title">Documento</span>
                  <p>{{ exemptionFile.name }}</p>
                </div>
                <div class="d-flex justify-content-end">
                  <CustomFileInput
                    v-model="exemptionFile.file"
                    accept=".pdf"
                    add-text="Presione aquí para añadir Documento"
                    :attachment-id="exemptionFile.fileId"
                    :can-delete="false"
                    :disabled="true"
                    display-style="normal"
                    :is-edit="true"
                    :name="exemptionFile.fileName"
                    :on-download="() => {}"
                  />
                  <div>
                    <action-button
                      v-if="
                        userHasPermissions(
                          'provider.dispenseManagement.replacedocument'
                        )
                      "
                      class="btn mr-2 ml-2 p-0 button-container"
                      i="fas fa-undo-alt ml-2"
                      variant="outline-success"
                      @click="
                        onReplaceFileHandler(`fileRef-${exemptionFile.fileId}`)
                      "
                    />
                    <input
                      :id="`file-${exemptionFile.fileId}`"
                      :ref="`fileRef-${exemptionFile.fileId}`"
                      accept=".pdf"
                      style="display: none"
                      type="file"
                      @change="
                        onUploadFileHandler(
                          exemptionFile.id,
                          `fileRef-${exemptionFile.fileId}`
                        )
                      "
                    />
                  </div>
                </div>
              </div>
            </callout>
          </div>
        </div>
      </template>
    </validation-observer>
  </div>
</template>

<script>
import moment from 'moment';
import SectionTitle from '@/components/common/page/SectionTitle';
import Supervised from './components/Supervised';
import CustomDatePicker from '@/components/common/form/CustomDatePicker';
import CustomTextArea from '@/components/common/form/CustomTextArea';
import Error from '@/components/common/Error.vue';
import {
  getNewExcemptionInfo,
  getExistingExcemptionInfo,
  saveSpecialistExcemption,
  replaceExemptionDocument,
} from '@/modules/provider/api.js';
import supervisorTaskTypes from '@/utils/constants/specialistSupervisorTaskTypes.js';
import alertMixin from '@/mixins/alert.mixin';
import supervisedTypes from '@/utils/constants/supervisedTypes.js';
import CustomFileInput from '@/components/common/CustomFileInput';
import exemptionType from '@/utils/shared/constants/exemptionType';
import { serialize as objectToFormData } from 'object-to-formdata';
import DispenseDocuments from './components/DispenseDocuments.vue';

export default {
  name: 'DispensationRequest',
  components: {
    SectionTitle,
    Supervised,
    CustomDatePicker,
    CustomTextArea,
    CustomFileInput,
    Error,
    DispenseDocuments,
  },
  mixins: [alertMixin],
  data: () => ({
    specialistIds: [],
    dispensationInfo: {},
    specialistSupervisions: [],
    minDateToValidate: null,
    maxDateToValidate: null,
    specialistSupervisorTaskOne: Object.keys(supervisorTaskTypes).map(
      (key) => ({
        id: supervisorTaskTypes[key].id,
        name: supervisorTaskTypes[key].name,
      })
    ),
    specialistSupervisorTaskTwo: Object.keys(supervisorTaskTypes).map(
      (key) => ({
        id: supervisorTaskTypes[key].id,
        name: supervisorTaskTypes[key].name,
      })
    ),
    requiredSameCorpRolMessage: '',
    requiredOtherCorpRolMessage: '',
    requiredValidRangeDate: '',
    readOnly: false,
    exemptionType: 0,
  }),
  computed: {
    isDeprExemption() {
      return (
        exemptionType.EspecialistaDEPR == this.dispensationInfo?.exemptionType
      );
    },
    isDEPRRequest() {
      return this.exemptionType == exemptionType.EspecialistaDEPR;
    },
    page() {
      return {
        title: 'Solicitud de Dispensa',
        icon: 'fas fa-user-tie',
        routes: ['Proveedor', 'Especialista'],
      };
    },
    validationMessages() {
      return {
        requiredCorpRole: 'Debe seleccionar al menos 1 opción',
        dateRangeNotValid: 'El rango de fecha seleccionado no es válido',
      };
    },
    exemptionMinDate() {
      if (!this.minDateToValidate) return null;
      const d = new Date(this.minDateToValidate);
      d.setDate(d.getDate() - 1);
      return d;
    },
    hasSameCorpRoles() {
      return (
        this.specialistSupervisorTaskOne.filter((x) => x.checked).length == 0
      );
    },
    hasOtherCorpRoles() {
      return (
        this.specialistSupervisorTaskTwo.filter((x) => x.checked).length == 0
      );
    },
    headerActions() {
      return [
        {
          component: 'ActionButton',
          variant: 'outline-success',
          text: 'Regresar',
          icon: 'fas fa-arrow-circle-left',
          click: () => this.$router.go(-1),
        },
        {
          component: 'ActionButton',
          variant: 'success',
          text: 'Guardar',
          icon: 'fas fa-save',
          visible: !this.readOnly,
          click: this.save,
        },
      ];
    },
    board() {
      return [
        [
          {
            label: 'Año Fiscal:',
            icon: 'fas fa-calendar-edit ',
            data: this.dispensationInfo?.fiscalYear,
          },
          {
            label: 'Compañía:',
            icon: 'fas fa-address-book',
            data: this.dispensationInfo?.companyName,
          },
          {
            label: 'Especialista:',
            icon: 'fas fa-address-book',
            data: this.dispensationInfo?.specialistName,
          },
          {
            label: 'Número Seguro Social:',
            icon: 'fas fa-ballot-check',
            data: this.dispensationInfo?.socialSecurityNo,
          },
        ],
      ];
    },
  },
  async mounted() {
    await this.initData();
  },
  methods: {
    async initData() {
      const {
        companyId,
        specialistId,
        academicYearId,
        specialistSupervisions,
        dispensationRequestId,
        exemptionType,
      } = this.$route.params;

      this.exemptionType = exemptionType ?? 0;

      if (dispensationRequestId === undefined) {
        this.specialistSupervisions = specialistSupervisions;
        this.specialistIds = specialistSupervisions
          .filter((x) => x.supervisedType == supervisedTypes.Specialist)
          .map((x) => x.supervisedId);
      } else {
        this.readOnly = true;
      }

      await this.getExcemptionInfo(
        companyId,
        specialistId,
        academicYearId,
        this.exemptionType,
        this.specialistIds,
        dispensationRequestId
      );
    },
    async getExcemptionInfo(
      companyId,
      specialistId,
      academicYearId,
      exemptionType,
      specialistIds,
      dispensationRequestId
    ) {
      const { data: exemptionInfo } =
        dispensationRequestId === undefined
          ? await getNewExcemptionInfo(
              companyId,
              specialistId,
              academicYearId,
              exemptionType,
              specialistIds
            )
          : await getExistingExcemptionInfo(dispensationRequestId);

      if (exemptionInfo) {
        this.dispensationInfo = exemptionInfo;
        this.minDateToValidate = exemptionInfo.minDate;
        this.maxDateToValidate = exemptionInfo.maxDate;
      }

      if (exemptionInfo.exemptionRequestId) {
        this.specialistIds = exemptionInfo.childSpecialistIds;

        this.specialistSupervisorTaskOne.forEach((x) => {
          x.checked = exemptionInfo.sameCorpRoles.some((s) => s == x.id);
        });

        this.specialistSupervisorTaskTwo.forEach((x) => {
          x.checked = exemptionInfo.otherCorpRoles.some((s) => s == x.id);
        });
      }
    },
    evalDateRange() {
      return (
        moment(this.dispensationInfo.selectedMinDate).isSameOrAfter(
          this.minDateToValidate
        ) &&
        moment(this.dispensationInfo.selectedMaxDate).isSameOrBefore(
          this.maxDateToValidate
        )
      );
    },
    buildFormData(payload) {
      const formData = new FormData();

      function appendFormData(key, value) {
        if (value === null || value === undefined) {
          return;
        }

        if (Array.isArray(value)) {
          value.forEach((item, index) => {
            if (typeof item === 'object' && item !== null) {
              // Array de objetos
              Object.keys(item).forEach((prop) => {
                appendFormData(`${key}[${index}].${prop}`, item[prop]);
              });
            } else {
              // Array simple
              formData.append(`${key}[${index}]`, item);
            }
          });
        } else if (typeof value === 'object') {
          // Objeto anidado
          Object.keys(value).forEach((prop) => {
            appendFormData(`${key}.${prop}`, value[prop]);
          });
        } else {
          // Propiedad simple
          formData.append(key, value);
        }
      }

      Object.keys(payload).forEach((key) => {
        appendFormData(key, payload[key]);
      });

      return formData;
    },

    async save() {
      const isValid = await this.$refs.dispensationRequestRef.validate();

      let isDispenseDocumentValid = true;

      if (this.isDEPRRequest) {
        isDispenseDocumentValid = await this.$refs.dispenseDocumentsRef.validateDocuments();
      } else {
        this.requiredSameCorpRolMessage = this.hasSameCorpRoles
          ? this.validationMessages.requiredCorpRole
          : '';
        this.requiredOtherCorpRolMessage = this.hasOtherCorpRoles
          ? this.validationMessages.requiredCorpRole
          : '';
        this.requiredValidRangeDate = !this.evalDateRange()
          ? this.validationMessages.dateRangeNotValid
          : '';
      }

      this.dispensationInfo.selectedMinDate = moment(
        this.dispensationInfo.selectedMinDate
      ).format('YYYY-MM-DD');
      this.dispensationInfo.selectedMaxDate = moment(
        this.dispensationInfo.selectedMaxDate
      ).format('YYYY-MM-DD');

      if (
        !isValid ||
        !isDispenseDocumentValid ||
        this.requiredSameCorpRolMessage ||
        this.requiredOtherCorpRolMessage ||
        this.requiredValidRangeDate
      ) {
        this.ShowToast('Alerta', 'Información Incompleta', 'error');
        return;
      }
      const payload = {
        ...this.dispensationInfo,
        sameCorpRoles: this.specialistSupervisorTaskOne
          .filter((x) => x.checked)
          .map((x) => x.id),
        otherCorpRoles: this.specialistSupervisorTaskTwo
          .filter((x) => x.checked)
          .map((x) => x.id),
        requestSpecialists: this.specialistSupervisions.map((x) => ({
          supervisedType: x.supervisedType,
          supervisedId: x.supervisedId,
        })),
        exemptionType: this.exemptionType,
      };

      const formData = this.buildFormData(payload);

      if (this.dispensationInfo.exemptionFiles) {
        this.dispensationInfo.exemptionFiles.forEach((fileDto, index) => {
          if (fileDto.file) {
            formData.append(`ExemptionFiles[${index}].File`, fileDto.file);
          }
        });
      }

      await saveSpecialistExcemption(formData)
        .then(() => {
          this.ShowToast(
            'Proceso Exitoso.',
            'Los datos han sido guardados exitosamente.',
            'success'
          );
          this.$router.go(-1);
        })
        .catch((error) => {
          this.ShowToast('Error', error.response.data.message, 'error');
          this.saveDisabled = false;
        });
    },
    onReplaceFileHandler(refName) {
      const ref = this.$refs[refName];

      if (Array.isArray(ref)) {
        ref[0]?.click?.(); // El ?. previene el error si no es función
      } else {
        ref?.click?.();
      }
    },
    async onUploadFileHandler(exemptionFileId, refName) {
      const ref = this.$refs[refName];

      let files = ref[0]?.files;

      if (files.length > 0) {
        const formData = {
          exemptionRequestId: this.dispensationInfo.exemptionRequestId,
          exemptionFileId: exemptionFileId,
          file: files[0],
        };

        const payload = objectToFormData(formData, { indices: true });

        await replaceExemptionDocument(payload);
        this.ShowToast(
          '',
          'El documento ha sido reemplazado exitosamente.',
          'success'
        );
        await this.initData();
      }
    },
  },
};
</script>

<style scoped>
.button-container {
  width: 33px;
  height: 33px;
}
</style>
